import { actions as groupActions } from 'containers/Group';
import is from 'is_js';
import { List } from 'immutable';

export function formatToRenderUI(idpList, timeZone) {
  if (! idpList || ! idpList.size || ! timeZone) return idpList;

  return idpList.map((idpRow) => Object.assign(
    {}, idpRow.update('period', (period) => getPeriodText(period, timeZone, 'list')).toJS(),
    formatSkillResults(idpRow.skillResults)));

  function formatSkillResults(results) {
    const sortedResult = results.sort(resortByScore);
    const skillResults = {};
    sortedResult.forEach((result, index) => {
      Object.assign(skillResults, {
        [`skill${index + 1}`]: `${result.name} ${formatSkillScore(result.score)}`,
      });
    });
    return skillResults;
  }
}

export function formatToRenderUIBySkillNameHFv4(idpList, timeZone) {
  if (! idpList || ! idpList.size || ! timeZone) return idpList;

  return idpList.map((idpRow) => Object.assign(
    {}, idpRow.update('period', (period) => getPeriodText(period, timeZone, 'list')).toJS(),
    formatSkillResultsByName(idpRow.skillResults)));

  function formatSkillResultsByName(results) {
    const skillResults = {};
    results.forEach((result) => {
      skillResults[result.name] = formatSkillScore(result.score);
    });
    return skillResults;
  }
}

export function getGroupIdpList(state, groupId) {
  const groupIdpList = state.get('rootReducer').get('individualDevelopmentPlanList');
  if (! groupId) return groupIdpList;
  return groupIdpList.filter((groupIdpInfo) => groupIdpInfo.groupId === groupId);
}

export function getIdpList(groupIdpList, groupIdpId) {
  if (! groupIdpList || groupIdpList.size === 0) return;
  const _groupIdp = groupIdpList.find((groupIdp) =>
    groupIdp.id.toString() === groupIdpId);
  if (! _groupIdp) return;
  return _groupIdp.idpList;
}

export function getIdpId(state, groupIdpId, studentId) {
  const groupIdpList = state.get('rootReducer').get('individualDevelopmentPlanList');
  const idpList = getIdpList(groupIdpList, groupIdpId);

  if (! idpList || ! idpList.size > 0 || ! studentId) return;
  const _idp = idpList.find((idp) => idp.studentId === studentId);
  return _idp && _idp.id && _idp.id.toString();
}

export function getIdpStatus(state, idpId) {
  const groupIdpList = state.get('rootReducer').get('individualDevelopmentPlanList');
  if (! groupIdpList || groupIdpList.size === 0) return;
  let idpList = new List();
  groupIdpList.forEach((groupIdp) => {
    idpList = idpList.concat(groupIdp.idpList);
  });
  if (idpList.size === 0) return;
  return idpList.find((idp) => idp.id.toString() === idpId.toString()).status;
}

export function getMissingInformation(state, idpId) {
  if (! idpId) return [];
  const idp = state.get('rootReducer').get('individualDevelopmentPlan').get(idpId);
  if (! idp || ! idp.check || idp.check.isPassed) return [];
  return idp.check.missingItems || [];
}

export function getDosRejectedComment(state, ownProps) {
  const { groupIdpId, studentId } = ownProps.params;
  const idpId = getIdpId(state, groupIdpId, studentId);
  const individualDevelopmentPlan =
    state.get('rootReducer').get('individualDevelopmentPlan').get(idpId);
  return individualDevelopmentPlan &&
    individualDevelopmentPlan.dosRejectedComment;
}

export function getStudentInfo(state, ownProps) {
  const { groupId, groupIdpId, studentId } = ownProps.params;
  const idpId = getIdpId(state, groupIdpId, studentId);
  const individualDevelopmentPlan =
    state.get('rootReducer').get('individualDevelopmentPlan').get(idpId);
  if (! individualDevelopmentPlan || ! individualDevelopmentPlan.period) return;

  const { timeZone, name: centre } = groupActions.getCentre(state, groupId);
  const { period } = individualDevelopmentPlan;
  const { groupSummary, number } = state.get('rootReducer').get('groups').get(groupId).data;
  const groupMembers = state.get('rootReducer').get('groupMembers');
  const status = getIdpStatus(state, idpId);

  if (! timeZone || ! studentId || ! groupMembers || ! period) return;
  const forPeriod = getPeriodText(period, timeZone, 'detail');
  const { firstName, lastName, localName } = groupMembers.get(studentId).get('studentGeneralInfo');
  const name = getStudentName(firstName, lastName, localName);
  const {
    mainTeacherName: teacherOne,
    coTeacherName: teacherTwo,
    pa: progressAdvisor,
  } = groupSummary;
  const course = number && number.substring(0, number.indexOf('-'));

  return {
    forPeriod,
    name,
    course,
    teacherOne,
    teacherTwo,
    progressAdvisor,
    centre,
    status,
    period,
  };
}

function getStudentName(firstName, lastName, localName) {
  return `${firstName} ${lastName} ${localName}`;
}

function getPeriodText(period, timeZone, type) {
  switch (type) {
    case 'list':
      return `${formatDate(period.start, timeZone, 'YYYY.MM.DD')}-${formatDate(period.end, timeZone, 'YYYY.MM.DD')}`;
    case 'detail':
      return `from ${formatDate(period.start, timeZone, 'YYYY-MM-DD')} to ` +
        `${formatDate(period.end, timeZone, 'YYYY-MM-DD')}`;
    default:
      return null;
  }
}

function formatDate(momentDate, timeZone, customFormat) {
  if (! momentDate) return;
  return momentDate.clone().tz(timeZone).format(customFormat);
}

export function getReportCardInfo(state, ownProps) {
  const { groupId, groupIdpId, studentId } = ownProps.params;
  const hasPaperBasedHomework = groupActions.hasPaperBasedHomework(state, groupId);
  const { timeZone } = groupActions.getCentre(state, groupId);
  const idpId = getIdpId(state, groupIdpId, studentId);
  const individualDevelopmentPlan =
    state.get('rootReducer').get('individualDevelopmentPlan').get(idpId);
  if (! individualDevelopmentPlan) return;
  const learningResult = individualDevelopmentPlan.get('learningResult');
  if (! learningResult) return;

  const { skillResults } = learningResult;

  const reportCard = {};
  reportCard.skillResults = skillResults.map((skillResult) =>
    skillResult.update('score', formatSkillScore));
  reportCard.lastUpdatedTimeUtc =
    formatDate(individualDevelopmentPlan.lastUpdatedTimeUtc, timeZone, 'YYYY-MM-DD');
  const {
    finishedPaperBasedHomework,
    totalPaperBasedHomework,
    finishedOnlineHomework,
    totalOnlineHomework,
    presentAttendence,
    totalAttendence,
  } = learningResult;
  if (hasPaperBasedHomework) {
    if (is.number(totalPaperBasedHomework) && is.number(finishedPaperBasedHomework)) {
      reportCard.homeworkCompletion =
        `${finishedPaperBasedHomework}/${totalPaperBasedHomework} Assignments`;
    } else {
      reportCard.homeworkCompletion = '–';
    }
  }

  if (is.number(finishedOnlineHomework) && is.number(totalOnlineHomework)) {
    reportCard.online =
      `${finishedOnlineHomework}/${totalOnlineHomework} Assignments`;
  } else {
    reportCard.online = '–';
  }

  if (is.number(presentAttendence) && is.number(totalAttendence)) {
    reportCard.attendance =
      `${presentAttendence}/${totalAttendence} Attended`;
  } else {
    reportCard.attendance = '–';
  }

  const quizAverageScore = calculateQuizAverageScore(skillResults);
  reportCard.totalScore = is.number(quizAverageScore) ? `${quizAverageScore}%` : '–';

  return reportCard;
}

export function calculateQuizAverageScore(skillResults) {
  if (! skillResults || skillResults.size <= 0) return null;

  let totalScore = 0;
  let scoreCount = 0;
  skillResults.forEach((skillResult) => {
    if (is.number(skillResult.score)) {
      totalScore += Math.min(100, skillResult.score * 100);
      scoreCount += 1;
    }
  });

  if (scoreCount === 0) return null;
  return Math.round(totalScore / scoreCount);
}

export function formatSkillScore(score, defaultText = '–') {
  if (is.not.number(score)) return defaultText;
  return `${Math.round(score * 100)}%`;
}

export function getSuggestionsInfo(state, ownProps) {
  const { groupId, groupIdpId, studentId } = ownProps.params;
  const { timeZone } = groupActions.getCentre(state, groupId);
  const idpId = getIdpId(state, groupIdpId, studentId);
  const individualDevelopmentPlan =
    state.get('rootReducer').get('individualDevelopmentPlan').get(idpId);
  if (! individualDevelopmentPlan) return;

  const suggestionsInfo = individualDevelopmentPlan.studentIDPFeedbackSuggestions;
  const learningResult = individualDevelopmentPlan.get('learningResult');
  if (! suggestionsInfo || ! learningResult) return;
  let { skillResults } = learningResult;
  skillResults = skillResults.sort(resortByScore);
  const {
    teacherId,
    overallSuggestion,
    suggestedTime,
    participationId,
    skillSuggestions,
  } = suggestionsInfo;

  let mergedSkillSuggestions = new List();
  if (skillResults.size > 0) {
    mergedSkillSuggestions = skillSuggestions.map((skillSuggestion) => {
      const mappedSkillResult =
        skillResults.find((skillResult) => skillResult.skillId === skillSuggestion.skillId);
      return skillSuggestion
        .set('name', mappedSkillResult && mappedSkillResult.name)
        .set('score', mappedSkillResult && mappedSkillResult.score);
    });
    mergedSkillSuggestions = mergedSkillSuggestions.sort(resortByScore);
  }

  const isSimplifiedTeacherFeedback = isHfv4ProgressReportOneOrTwo(state, groupId, groupIdpId);

  return {
    teacherId,
    overallSuggestion,
    suggestedTime,
    participationId,
    skillSuggestions: mergedSkillSuggestions,
    skillResults,
    timeZone,
    isSimplifiedTeacherFeedback,
  };
}

export function shouldShowSuggestions(groupIdpList, groupIdpId, studentId) {
  if (! groupIdpId || ! groupIdpList || groupIdpList.size === 0) return;
  const idpList = groupIdpList.get(parseInt(groupIdpId, 10)).idpList;
  if (idpList.size === 0) return;
  const idpSummary = idpList.find((_idp) => _idp.studentId === studentId);
  if (! idpSummary) return;
  return idpSummary.shouldShowSuggestions;
}

function mergeIdpApprovalRow(idpList, idpDetailList, members, idpId) {
  if (idpList.size === 0) return;
  const idpSummary = idpList.find((idp) => idp.id.toString() === idpId);
  const idpDetail = idpDetailList.get(idpId);
  if (! idpSummary || ! idpDetail) return;

  const { studentIDPFeedbackSuggestions: feedbackSuggestion } = idpDetail;
  const { studentId, skillResults } = idpSummary;
  const {
    teacherId,
    overallSuggestion,
    suggestedTime,
    participationId,
  } = feedbackSuggestion;
  let { skillSuggestions } = feedbackSuggestion;

  const { studentGeneralInfo: studentInfo } =
      members.find((member) => member.studentGeneralInfo.id === studentId);
  skillSuggestions = skillSuggestions.map((skillSuggestion) => {
    const skillResult = skillResults.find((result) => result.skillId === skillSuggestion.skillId);
    return skillSuggestion.merge(skillResult);
  });
  skillSuggestions = skillSuggestions.sort(resortByScore);
  return {
    idpId,
    studentInfo,
    teacherId,
    overallSuggestion,
    suggestedTime,
    participationId,
    skillSuggestions,
    shouldShowSuggestions: idpSummary.shouldShowSuggestions,
  };
}

export function getApprovalContent(idpList, idpDetailList, members, idpId) {
  const row = mergeIdpApprovalRow(idpList, idpDetailList, members, idpId);
  if (! row) return;
  return {
    idpId: row.idpId,
    studentInfo: row.studentInfo,
    teacherId: row.teacherId,
    overallSuggestion: row.overallSuggestion,
    suggestedTime: row.suggestedTime,
    participationId: row.participationId,
    skillSuggestions: row.skillSuggestions,
    shouldShowSuggestions: row.shouldShowSuggestions,
  };
}

function resortByScore(a, b) {
  return a.score - b.score;
}

export function getReportName(state, groupIdpId, defaultName = '') {
  const groupIdpList = getGroupIdpList(state);
  if (! groupIdpList) return defaultName;
  const groupIdpInfo = groupIdpList.get(parseInt(groupIdpId, 10));
  if (! groupIdpInfo) return defaultName;
  return groupIdpInfo.name;
}

const HFV4_SIMPLIFIED_TEACHER_FEEDBACK_NAMES = ['progress report 1', 'progress report 2'];

export function isHfv4ProgressReportOneOrTwo(state, groupId, groupIdpId) {
  if (! groupId || ! groupIdpId || ! groupActions.isHFV4CourseTypeGroup(state, groupId)) {
    return false;
  }
  const normalized = (getReportName(state, groupIdpId) || '').trim().toLowerCase();
  return HFV4_SIMPLIFIED_TEACHER_FEEDBACK_NAMES.includes(normalized);
}

export function getApprovalContentHFv4(
  state,
  groupId,
  groupIdpId,
  idpList,
  idpDetailList,
  members,
  idpId,
) {
  const row = mergeIdpApprovalRow(idpList, idpDetailList, members, idpId);
  if (! row) return;
  return Object.assign({}, row, {
    isSimplifiedTeacherFeedback: isHfv4ProgressReportOneOrTwo(state, groupId, groupIdpId),
  });
}

export function isGroupIdpFetched(state, groupIdpId) {
  const groupIdpList = state.get('rootReducer').get('individualDevelopmentPlanList');
  return is.not.undefined(groupIdpList.get(parseInt(groupIdpId, 10)));
}

function isEmpty(value) {
  return is.falsy(value) || (is.existy(value) && is.empty(value));
}

export function getEmptyActions(
  suggestionsInfo,
  shouldShowSuggestion,
  isSkillSuggestionManualOnly) {
  const {
    overallSuggestion,
    participationId,
    skillSuggestions,
    isSimplifiedTeacherFeedback,
  } = suggestionsInfo;
  let result = 0;

  if (isSimplifiedTeacherFeedback) {
    return isEmpty(overallSuggestion) ? 1 : 0;
  }

  if (isEmpty(overallSuggestion)) {
    result += 1;
  }

  if (isEmpty(participationId)) {
    result += 1;
  }

  if (shouldShowSuggestion && skillSuggestions && skillSuggestions.size > 0) {
    result += skillSuggestions.reduce((emptyNumber, skillSuggestion) => {
      const { selectedRecommendationList, suggestion } = skillSuggestion;
      if (isSkillSuggestionManualOnly) {
        if (isEmpty(suggestion)) {
          return emptyNumber + 1;
        }
      } else if (selectedRecommendationList.size <= 0) {
        return emptyNumber + 1;
      }

      return emptyNumber;
    }, 0);
  }
  return result;
}

export function isSuggestionsInfoComplete(state, ownProps) {
  const { groupId, groupIdpId, studentId } = ownProps.params;
  const groupIdpList = state.get('rootReducer').get('individualDevelopmentPlanList');
  const shouldShowSuggestion = shouldShowSuggestions(groupIdpList, groupIdpId, studentId);
  const isSkillSuggestionManualOnly = !! groupActions.isFRV2CourseTypeGroup(state, groupId);
  const suggestionsInfo = getSuggestionsInfo(state, ownProps);

  if (suggestionsInfo && suggestionsInfo.isSimplifiedTeacherFeedback) {
    const emptyActions = getEmptyActions(
      suggestionsInfo,
      shouldShowSuggestion,
      isSkillSuggestionManualOnly);
    return emptyActions === 0;
  }

  if (shouldShowSuggestion) {
    const skillSuggestions = suggestionsInfo && suggestionsInfo.skillSuggestions;
    if (! skillSuggestions || skillSuggestions.size <= 0) {
      return false;
    }
  }
  const emptyActions = getEmptyActions(
    suggestionsInfo,
    shouldShowSuggestion,
    isSkillSuggestionManualOnly);
  return emptyActions === 0;
}

export default formatToRenderUI;



// WEBPACK FOOTER //
// ./data/IndividualDevelopmentPlans/accessors.js